/**
 * Lo puro de la pantalla del análisis a fondo de los mails (`components/AnalisisEmails.tsx`): textos e intensidades.
 * Los números los calcula el backend (`util/analisis.util.ts`); acá sólo se dicen.
 */
import type { AnalisisEmails, CeldaCalor } from "../types";

export const DIAS_CORTOS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"] as const;

/** Lo que pesa cada celda del mapa: una respuesta dice más que un clic, y un clic más que una apertura. */
export const pesoCelda = (c: CeldaCalor) => c.aperturas + c.clics * 3 + c.respuestas * 5;

/** De 0 (nada) a 4 (lo más alto del mapa), para el color de la celda. Con un mapa vacío, todo 0. */
export function intensidad(c: CeldaCalor, max: number): 0 | 1 | 2 | 3 | 4 {
  const p = pesoCelda(c);
  if (p <= 0 || max <= 0) return 0;
  const r = p / max;
  return r > 0.75 ? 4 : r > 0.5 ? 3 : r > 0.25 ? 2 : 1;
}

export const maxDelMapa = (mapa: readonly CeldaCalor[]) => mapa.reduce((m, c) => Math.max(m, pesoCelda(c)), 0);

/** «45 min», «3 h», «2 días»: una mediana de demora en palabras. */
export function textoDemora(min: number | null | undefined): string {
  if (min === null || min === undefined || !Number.isFinite(min)) return "—";
  if (min < 60) return `${Math.max(1, Math.round(min))} min`;
  if (min < 48 * 60) return `${Math.round(min / 60)} h`;
  const d = Math.round(min / 1440);
  return `${d} ${d === 1 ? "día" : "días"}`;
}

/**
 * Cuántas de las aperturas que cuenta Snov son de personas, en palabras; null si no hubo ninguna automática (no hay
 * nada que corregir). El porqué lo dice el hallazgo de arriba: acá va el número que queda.
 */
export function textoAutomaticas(a: AnalisisEmails["automaticas"]): string | null {
  if (!a.aperturasTotales || !a.aperturas) return null;
  const n = (x: number) => x.toLocaleString("es-AR");
  const reales = a.aperturasTotales - a.aperturas;
  return `de las ${n(a.aperturasTotales)} aperturas que cuenta Snov, ${n(reales)} son de personas. Las otras ${n(a.aperturas)} llegaron a menos de un minuto del envío y no cuentan en las tasas de abajo.`;
}

/** El total de las respuestas que clasificó Snov; 0 si no hay datos. */
export const totalInteres = (i: AnalisisEmails["interes"]) => (i ? i.interesados + i.quizas + i.noInteresados : 0);
